'use client';

import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useAuth } from 'react-oidc-context';
import styled from 'styled-components';

import { Close } from '@zoomit/icons';
import { Image } from '@zoomit/primitives';
import { Box, Flex, Spacing } from '@zoomit/primitives';
import type {
  BaseImageType,
  TopicInfoType} from '@zoomit/web-kit';
import {
  BaseMediaUrl,
  isMobile,
  Rating,
  useFeedbackCreateMutation,
  useFeedbacksInfiniteQuery,
  useFeedbackThread,
  useFeedbackUpdateMutation,
  useReviewInfo,
} from '@zoomit/web-kit';
import { Button, cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { getFeedbackInfo } from '../../helpers/feedbacks/getFeedbackInfo';
import { FeedbackEditor } from '../feedbacks/editor/FeedbackEditor';
import { useInitializeFeedbackEditor } from '../feedbacks/editor/hooks/useInitializeFeedbackEditor';

type Props = {
  onClose: () => void;
  rate?: number;
  image: BaseImageType;
  title: string;
  topicInfo: TopicInfoType;
  minCharacter?: number;
  topicType: 'Product' | 'Store';
};

const maxLength = 1000;

export const ReviewModalBody = (props: Props) => {
  const { image, onClose, title, topicInfo, minCharacter, topicType } = props;
  const { isLoading } = useAuth();
  const params = useSearchParams();

  useEffect(() => {
    setValue('rate', props.rate || 0);
  }, [props.rate]);

  const { data: reviewData, refetch } = useReviewInfo({
    topicId: topicInfo.topicId,
    enabled: !isLoading,
    topicType,
    onSuccess: (d) => {
      editor?.commands.setContent({
        type: 'doc',
        content: d.review?.ranges || [],
      });
    },
  });

  const formMethods = useForm({
    defaultValues: {
      rate: props.rate || 0,
    },
  });
  const { handleSubmit, watch, setValue, reset } = formMethods;

  const feedbacksQuery = useFeedbacksInfiniteQuery(topicInfo.topicId, props.topicType);
  const feedbackThreadQuery = useFeedbackThread(params?.get('thread') as string, topicInfo);

  const createMutation = useFeedbackCreateMutation({
    topicType,
    topicInfo,
    onSuccess: () => {
      onSendComment();
      toast.success('امتیاز و نظر شما ثبت شد');
      onClose();
    },
  });

  const updateMutations = useFeedbackUpdateMutation({
    onSuccess: () => {
      onSendComment();
      toast.success('امتیاز و نظر شما ویرایش شد');
      onClose();
    },
  });

  const onSendComment = () => {
    refetch();
    feedbackThreadQuery.refetch();
    feedbacksQuery.refetch();
  };

  const placeholder = useMemo(() => {
    if (minCharacter) {
      return `نظر خود را اینجا وارد کنید ${minCharacter && `(حداقل ${minCharacter} کاراکتر)`}`;
    }
    return 'متن را اینجا وارد کنید';
  }, [minCharacter]);

  const { editor } = useInitializeFeedbackEditor({
    placeholder,
    limit: maxLength,
    topicInfo,
  });

  const onSubmit = () => {
    if (!editor) return;
    if (!reviewData) return;
    const data = formMethods.getValues();

    const { content, mentions, ranges } = getFeedbackInfo(editor);

    if (minCharacter && content.length > 0 && content.length < minCharacter) {
      return toast.error(`حداقل ${minCharacter} کاراکتر برای نوشتن بررسی نیاز است`);
    }

    if (reviewData.review) {
      updateMutations.mutate({
        content: content,
        feedbackType: 'Review',
        feedbackId: reviewData.review.id,
        score: data.rate,
        ranges,
        mentions,
      });
    } else {
      createMutation.mutate({
        content: content,
        topicType,
        isQuestion: false,
        feedbackToReply: undefined,
        score: data.rate,
        ranges,
        mentions,
      });
    }
  };

  const paddingInline = isMobile ? 'x11' : 'x6';
  const textAlign = isMobile ? 'center' : 'right';
  const gap = isMobile ? 1.6 : 0.6;

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box backgroundColor={cssVar(theme.background2)}>
          <TopSection alignItems="center" justifyContent="space-between">
            <Title variant="h5">امتیاز و بررسی</Title>
            {!isMobile && (
              <Close
                onClick={() => {
                  reset();
                  onClose();
                }}
                className={'cursor-pointer'}
                size="lg"
              />
            )}
          </TopSection>
          <Box spacing={{ px: paddingInline, py: 'x6' }}>
            <Flex
              gap={gap}
              flexWrap="nowrap"
              flexDirection={isMobile ? 'column' : 'row'}
              spacing={{ mb: 'x10' }}
              alignItems="center"
            >
              <Image
                alt=""
                style={{ objectFit: 'contain' }}
                src={`${BaseMediaUrl}/${image.id}`}
                width={96}
                height={64}
              />
              <Typography textAlign={textAlign} lineHeight={1.8} variant="callout-bold">
                {title}
              </Typography>
            </Flex>
            <Flex gap={Spacing.x6} flexDirection="column" spacing={{ mb: 'x10' }} alignItems="center">
              <Typography variant="h6">به این محصول چه امتیازی می‌دی؟</Typography>
              <Rating
                justifyContent={isMobile ? 'center' : 'flex-start'}
                gap={isMobile ? Spacing.x6 : 0.2}
                rate={watch('rate')}
                onSelect={(rate) => setValue('rate', ++rate)}
                size={'xl'}
              />
            </Flex>
            <Typography textAlign={textAlign} lineHeight={2} tag="p" spacing={{ mb: 'x6' }} variant="callout">
              امتیاز و نظر تو درباره این محصول به سایر کاربران کمک می‌کنه بهتر تصمیم بگیرند
            </Typography>
            <Typography variant="subhead-bold" spacing={{ mb: 'x3' }} tag="p">
              نظر شما
              {!minCharacter && (
                <Typography variant="subhead" textColor={cssVar(theme.grey1)}>
                  {' '}
                  (اختیاری)
                </Typography>
              )}
            </Typography>
            <Box className={'border-grey3 border'}>
              <FeedbackEditor maxLength={maxLength} editor={editor} />
            </Box>
          </Box>
          <BottomSection gap={gap} flexWrap="nowrap" spacing={{ px: paddingInline, py: 'x4' }}>
            <Button
              isLoading={createMutation.isLoading || updateMutations.isLoading}
              type="submit"
              size={isMobile ? 'full width' : 'medium'}
            >
              {reviewData?.review ? 'ویرایش نظر' : 'ثبت نظر'}{' '}
            </Button>
            <Button
              onClick={onClose}
              size={isMobile ? 'medium' : 'small'}
              variant="outline"
              colorScheme="secondary"
            >
              انصراف
            </Button>
          </BottomSection>
        </Box>
      </form>
    </FormProvider>
  );
};

const TopSection = styled(Flex)`
  padding-inline: ${isMobile ? Spacing.x11 : Spacing.x6}rem;
  padding-block: ${isMobile ? Spacing.x2 : Spacing.x5}rem;
  ${!isMobile && `border-bottom: 1px solid ${cssVar(theme.grey6)}`}
`;

const Title = styled(Typography)`
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0.7rem;
    right: -2.4rem;
    transform: translateY(-50%);
    width: 0.4rem;
    height: 2.4rem;
    background-color: ${cssVar(theme.primary1)};
  }
`;

const BottomSection = styled(Flex)`
  background-color: ${cssVar(theme.grey7)};
`;
